<script setup lang="ts">
import '@/assets/css/vendors/toastify.css';
import { type HTMLAttributes, nextTick, onMounted, ref, watch } from 'vue';
import { init, reInit } from '@/components/Base/Notification/notification';
import type Toastify from 'toastify-js';
import { type Options } from 'toastify-js';
import Lucide from '@/components/Base/Lucide';
import { useGlobalSuccess } from '@/composables/useGlobalSuccess';

export interface GlobalSuccessNotificationElement extends HTMLDivElement {
  toastify: ReturnType<typeof Toastify>;
  showToast: () => void;
  hideToast: () => void;
}

export interface GlobalSuccessNotificationProps extends /* @vue-ignore */ HTMLAttributes {
  options?: Options;
  refKey?: string;
}

const props = defineProps<GlobalSuccessNotificationProps>();

const { globalSuccess, clearSuccess } = useGlobalSuccess();
const notificationRef = ref<GlobalSuccessNotificationElement>();

// Custom directive for this component
const vGlobalSuccessNotificationDirective = {
  mounted(el: GlobalSuccessNotificationElement) {
    // Initialize with success-specific options
    const successOptions: Options = {
      duration: 3000,
      gravity: 'top',
      position: 'right',
      ...props.options,
    };
    init(el, { ...props, options: successOptions });
  },
  updated(el: GlobalSuccessNotificationElement) {
    reInit(el);
  },
};

// Watch for changes to show/hide notification
watch(globalSuccess, async (val) => {
  if (val && notificationRef.value) {
    await nextTick();
    notificationRef.value.showToast();
    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (notificationRef.value) {
        notificationRef.value.hideToast();
      }
      clearSuccess();
    }, 3000);
  }
});

const closeSuccess = () => {
  if (notificationRef.value) {
    notificationRef.value.hideToast();
  }
  clearSuccess();
};

onMounted(() => {
  // Component is ready
});
</script>

<template>
  <div
    v-global-success-notification-directive
    ref="notificationRef"
    class="hidden py-5 pl-5 bg-white border rounded-lg shadow-xl pr-14 border-slate-200/60 dark:bg-darkmode-600 dark:text-slate-300 dark:border-darkmode-600"
  >
    <div class="flex">
      <Lucide icon="CheckCircle" class="text-success" />
      <div class="ml-4 mr-4">
        <div class="font-medium">Success!</div>
        <div class="mt-1 text-slate-500">
          {{ globalSuccess }}
        </div>
      </div>
    </div>
    <button
      type="button"
      class="absolute top-0 bottom-0 right-0 px-4 py-3 text-slate-400 font-bold hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
      @click="closeSuccess"
      data-dismiss="notification"
      aria-label="Close"
    ></button>
  </div>
</template>
